import { Ref, h, ref } from 'vue';

import {
  TSBadge,
  TSButtonToggle,
  TSImage,
  TSInputNumber,
  TSInputText,
  TSTagType,
  formatDate,
  getImageURL,
} from 'tsv2-library';
import {
  TableCellComponent,
  TableColumn,
} from 'tsv2-library/dist/src/components/v2/DataTable/DataTable.vue';

import SelectUser from '@/components/common/SelectUser.vue';

import {
  BorrowingBorrowedByAsset,
  BorrowingBorrowedByBorrower,
  BorrowingBorrowedReturnAsset,
  BorrowingHistoryByAsset,
  BorrowingPrelist,
  BorrowingRequest,
  BorrowingRequestFormData,
  BorrowingRequestHandover,
  BorrowingTransaction,
  BorrowingTransactionRequest,
  BorrowingTransactionRequestFormData,
} from '@/types/borrowing.type';
import { SettingTransaction } from '@/types/setting-transaction.type';

const EmptyComponent = h('span', '-');

const optionalValue = (value?: string): string => value ?? '-';

type ExtendedTableColumn = TableColumn & {
  forExclude?: string[] | number[];
  for?: string[] | number[];
};

export const prelistColumns = (): Ref<TableColumn[]> => {
  return ref([
    {
      header: 'Borrower',
      field: 'borrower.fullName',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingPrelist): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.borrower.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      header: 'Group',
      field: 'group.name',
      fixed: false,
      sortable: true,
      bodyComponent: (data: BorrowingPrelist): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.group.name,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      header: 'Total Asset',
      field: 'totalAssets',
      fixed: false,
      sortable: true,
      bodyTemplate: (data: BorrowingPrelist): string => {
        return data.totalAssets ? data.totalAssets.toLocaleString() : '-';
      },
    },
  ]);
};

export const requestColumns = (
  formData: Ref<Map<string, BorrowingRequestFormData>>,
  settingTransaction: Ref<SettingTransaction>,
  type?: 'Edit Borrowing Request' | 'Transaction Borrowing Request',
): Ref<ExtendedTableColumn[]> => {
  return ref(
    [
      {
        header: 'Photo',
        field: 'assetImageSmall',
        fixed: true,
        sortable: true,
        excluded: true,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          return {
            component: TSImage,
            props: {
              thumbnail: getImageURL(data.assetImageSmall),
              preview: getImageURL(data.assetImageBig),
              class: '!w-7 !h-7',
            },
          };
        },
      },
      {
        header: 'Name',
        field: 'name.name',
        fixed: true,
        sortable: true,
        bodyTemplate: (data: BorrowingRequest): string => {
          return data.name.nameWithSequence ? data.name.nameWithSequence : '-';
        },
      },
      {
        header: 'Brand',
        field: 'brand.name',
        fixed: false,
        sortable: true,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          return data.brand.name
            ? {
                component: TSBadge,
                props: {
                  label: data.brand.name,
                  severity: 'dark',
                  format: 'nowrap',
                },
              }
            : {
                component: EmptyComponent,
              };
        },
      },
      {
        header: 'Model/Type',
        field: 'model.name',
        fixed: false,
        sortable: true,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          return data.model.name
            ? {
                component: TSBadge,
                props: {
                  label: data.model.name,
                  severity: 'dark',
                  format: 'nowrap',
                },
              }
            : {
                component: EmptyComponent,
              };
        },
      },
      {
        forExclude: ['Edit Borrowing Request'],
        header: 'Group',
        field: 'group.name',
        fixed: false,
        sortable: true,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          return data.group.name
            ? {
                component: TSBadge,
                props: {
                  label: data.group.name,
                  severity: 'primary',
                  format: 'nowrap',
                },
              }
            : {
                component: EmptyComponent,
              };
        },
      },
      {
        forExclude: ['Edit Borrowing Request'],
        header: 'Borrower',
        field: 'borrower.fullName',
        fixed: true,
        sortable: false,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          const fData = formData.value.get(data._id);

          return {
            component: SelectUser,
            props: {
              'id': data._id,
              'modelValue': fData?.borrower,
              'onUpdate:modelValue': (value: {
                _id: string;
                fullName: string;
                key: number;
              }): void => {
                if (fData) {
                  formData.value.set(data._id, { ...fData, borrower: value });
                }
              },
            },
          };
        },
      },
      {
        header: 'Duration (Day)',
        excluded: true,
        field: '',
        fixed: true,
        sortable: false,
        bodyComponent: (data: BorrowingRequest): TableCellComponent => {
          const fData = formData.value.get(data._id);

          return {
            component: TSInputNumber,
            props: {
              'min': 1,
              'value': fData?.duration,
              'max': settingTransaction.value.borrowingAttrs.maxBorrowingTime,
              'size': 'small',
              'useValidator': true,
              'invalid': !fData?.duration,
              'placeholder': 'Enter duration',
              'modelValue': fData?.duration,
              'onUpdate:modelValue': (value: number): void => {
                if (fData) {
                  formData.value.set(data._id, { ...fData, duration: value });
                }
              },
            },
          };
        },
      },
    ].filter((col) => !col.forExclude?.includes(type ?? '')),
  );
};

export const transactionColumns = (): Ref<TableColumn[]> => {
  return ref<TableColumn[]>([
    {
      field: 'transactionId',
      header: 'Transaction ID',
      fixed: true,
      sortable: true,
      bodyTemplate(data: BorrowingTransaction): string {
        return data.transactionId ? data.transactionId : '-';
      },
    },
    {
      field: 'status',
      header: 'Status',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingTransaction): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.status,
            format: 'nowrap',
            severity: getStatusSeverity(
              data as unknown as BorrowingTransactionRequest,
              'status',
            ),
          },
        };
      },
    },
    {
      field: 'group.name',
      header: 'Group',
      fixed: false,
      sortable: true,
      bodyComponent(data: BorrowingTransaction): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.group.name,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'borrower.fullName',
      header: 'Borrower',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingTransaction): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.borrower.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'manager.fullName',
      header: 'Manager',
      fixed: false,
      sortable: true,
      bodyComponent(data: BorrowingTransaction): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.manager.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'totalAssets',
      header: 'Total Asset',
      fixed: true,
      sortable: true,
      bodyTemplate(data: BorrowingTransaction): string {
        return data.totalAssets ? data.totalAssets.toLocaleString() : '-';
      },
    },
    {
      field: 'createdAt',
      header: 'Request Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate(data: BorrowingTransaction): string {
        return formatDate(data.createdAt, true);
      },
    },
    {
      field: 'updatedAt',
      header: 'Last Update',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate(data: BorrowingTransaction): string {
        return formatDate(data.updatedAt, true);
      },
    },
  ]);
};

export const handoverColumns = (step: number): ExtendedTableColumn[] => {
  return [
    {
      header: 'Photo',
      field: 'assetImageSmall',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: data.assetImageSmall,
            preview: data.assetImageBig,
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      header: 'Name',
      field: 'name.nameWithSequence',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingRequestHandover): string => {
        return data.name.nameWithSequence ? data.name.nameWithSequence : '-';
      },
    },
    {
      header: 'Brand',
      field: 'brand.name',
      fixed: false,
      sortable: true,
      bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Model/Type',
      field: 'model.name',
      fixed: false,
      sortable: true,
      bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      forExclude: [2],
      header: 'TAG',
      field: 'tagType',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
        return {
          component: TSTagType,
          props: {
            qr: data.qr,
            rfid: data.rfid,
          },
        };
      },
    },
    {
      header: 'Status',
      field: 'isVerified',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: ((): string => {
              if (data.status === 'TAG Reported') {
                return data.status;
              }
              return data.isVerified ? 'Verified' : 'Unverified';
            })(),
            severity: ((): string => {
              if (data.status === 'TAG Reported') {
                return 'danger';
              }
              return data.isVerified ? 'success' : 'warning';
            })(),
            format: 'nowrap',
          },
        };
      },
    },
    {
      header: 'Duration',
      field: 'duration',
      fixed: false,
      sortable: true,
      bodyTemplate: (data: BorrowingRequestHandover): string => {
        return `${data.duration} Day(s)`;
      },
    },
  ].filter((col) => !col.forExclude?.includes(step));
};

export const detailColumns = (
  isBorrowed?: boolean,
): Ref<ExtendedTableColumn[]> => {
  const options: ExtendedTableColumn[] = [
    {
      field: 'assetImageSmall',
      header: 'Photo',
      fixed: false,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      field: 'name.nameWithSequence',
      header: 'Name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return data.name.nameWithSequence ?? '-';
      },
    },
    {
      field: 'brand.name',
      header: 'Brand',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      field: 'model.name',
      header: 'Model/Type',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      field: 'status',
      header: 'Status',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.status,
            format: 'nowrap',
            severity: getStatusSeverity(data, 'status'),
          },
        };
      },
    },
    {
      field: 'duration',
      header: 'Duration',
      fixed: false,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return `${data.duration.toString() ?? '-'} day(s)`;
      },
    },
  ];

  if (isBorrowed) {
    options.push(
      ...[
        {
          field: 'dueDate',
          header: 'Due Return Date',
          fixed: true,
          sortable: true,
          dateValue: true,
          bodyTemplate: (data: BorrowingTransactionRequest): string => {
            return formatDate(data.dueDate);
          },
        },
        {
          field: 'returnDate',
          header: 'Actual Return Date',
          fixed: false,
          sortable: true,
          dateValue: true,
          bodyTemplate: (data: BorrowingTransactionRequest): string => {
            return data.returnDate ? formatDate(data.returnDate, true) : '-';
          },
        },
        {
          field: 'remainingDay',
          header: 'Note',
          fixed: false,
          sortable: true,
          bodyComponent(data: BorrowingTransactionRequest): TableCellComponent {
            return data.remainingDay > -1
              ? {
                  component: h(
                    'span',
                    null,
                    `Remaining Time: ${data.remainingDay} day(s)`,
                  ),
                }
              : {
                  component: h(
                    'span',
                    {
                      class: 'text-danger-500',
                    },
                    `Overtime: ${data.remainingDay} day(s)`,
                  ),
                };
          },
        },
      ],
    );
  }

  return ref<ExtendedTableColumn[]>(options);
};

export const byAssetColumns = (): Ref<TableColumn[]> => {
  return ref<TableColumn[]>([
    {
      field: 'assetImageSmall',
      header: 'Photo',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (data: BorrowingHistoryByAsset): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      field: 'name.nameWithSequence',
      header: 'Name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingHistoryByAsset): string => {
        return data.name.nameWithSequence ?? '-';
      },
    },
    {
      field: 'status',
      header: 'Status',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingHistoryByAsset): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.status,
            format: 'nowrap',
            severity: getStatusSeverity(
              data as unknown as BorrowingTransactionRequest,
              'status',
            ),
          },
        };
      },
    },
    {
      header: 'Group',
      field: 'group.name',
      fixed: false,
      sortable: true,
      bodyComponent: (data: BorrowingHistoryByAsset): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.group.name,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      header: 'Borrower',
      field: 'borrower.fullName',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingHistoryByAsset): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.borrower.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      header: 'Manager',
      field: 'manager.fullName',
      fixed: false,
      sortable: true,
      bodyComponent: (data: BorrowingHistoryByAsset): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.manager.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'createdAt',
      header: 'Request Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate: (data: BorrowingHistoryByAsset): string => {
        return data.createdAt ? formatDate(data.createdAt, true) : '-';
      },
    },
    {
      header: 'Duration',
      field: 'duration',
      fixed: false,
      sortable: true,
      bodyTemplate: (data: BorrowingHistoryByAsset): string => {
        return `${data.duration} Day(s)`;
      },
    },
    {
      field: 'dueDate',
      header: 'Due Return Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate: (data: BorrowingHistoryByAsset): string => {
        return data.dueDate ? formatDate(data.dueDate, true) : '-';
      },
    },
    {
      field: 'returnDate',
      header: 'Actual Return Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate: (data: BorrowingHistoryByAsset): string => {
        return data.returnDate ? formatDate(data.returnDate, true) : '-';
      },
    },
  ]);
};

export const confirmColumns = (): TableColumn[] => {
  return [
    {
      header: 'Photo',
      field: 'assetImageSmall',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      header: 'Name',
      field: 'name.name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return data.name.nameWithSequence ? data.name.nameWithSequence : '-';
      },
    },
    {
      header: 'Brand',
      field: 'brand.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Model/Type',
      field: 'model.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Duration',
      field: 'duration',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return `${data.duration} Day(s)`;
      },
    },
  ];
};

export const extendRequestColumns = (
  formData: Ref<Map<string, BorrowingTransactionRequest>>,
  settingTransaction: Ref<SettingTransaction>,
): TableColumn[] => {
  return [
    {
      field: 'assetImageSmall',
      header: 'Photo',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      field: 'name.nameWithSequence',
      header: 'Name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return data.name.nameWithSequence ?? '-';
      },
    },
    {
      field: 'brand.name',
      header: 'Brand',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      field: 'model.name',
      header: 'Model/Type',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      field: 'dueDateInt',
      header: 'Due Return Date',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: h(
            'span',
            {
              class:
                Date.parse(new Date().toString()) > (data.dueDateInt ?? 0)
                  ? 'text-danger-500'
                  : 'text-general-800',
            },
            formatDate(data.dueDate, false),
          ),
        };
      },
    },
    {
      field: 'duration',
      header: 'Duration (Day)',
      fixed: true,
      sortable: false,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        const fData = formData.value.get(data._id);

        return {
          component: TSInputNumber,
          props: {
            'min': 1,
            'value': fData?.duration,
            'max': settingTransaction.value.borrowingAttrs.maxBorrowingTime,
            'size': 'small',
            'useValidator': true,
            'invalid': !fData?.duration,
            'placeholder': 'Enter duration',
            'modelValue': fData?.duration,
            'onUpdate:modelValue': (value: number): void => {
              if (fData) {
                formData.value.set(data._id, { ...fData, duration: value });
              }
            },
          },
        };
      },
    },
  ];
};

export const extendApprovalColumns = (
  formData: Ref<Map<string, BorrowingTransactionRequestFormData>>,
  settingTransaction: Ref<SettingTransaction>,
): Ref<TableColumn[]> => {
  return ref<TableColumn[]>([
    {
      header: 'Photo',
      field: 'assetImageSmall',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      header: 'Name',
      field: 'name.nameWithSequence',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequestFormData): string => {
        return data.name.nameWithSequence ? data.name.nameWithSequence : '-';
      },
    },
    {
      header: 'Brand',
      field: 'brand.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Model/Type',
      field: 'model.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Duration',
      field: 'duration',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequestFormData): string => {
        return `${data.duration} Day(s)`;
      },
    },
    {
      header: 'Approve Duration (Day)',
      field: 'approvedDuration',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        const fData = formData.value.get(data._id);

        return {
          component: TSInputNumber,
          props: {
            'min': 0,
            'value': fData?.approvedDuration,
            'max': settingTransaction.value.borrowingAttrs.maxBorrowingTime,
            'size': 'small',
            'useValidator': true,
            'disabled': fData?.isApproved === undefined,
            'invalid': typeof fData?.approvedDuration !== 'number',
            'placeholder': 'Enter duration',
            'modelValue': fData?.approvedDuration,
            'onUpdate:modelValue': (value: number): void => {
              if (fData) {
                formData.value.set(data._id, {
                  ...fData,
                  approvedDuration: value,
                });
              }
            },
          },
        };
      },
    },
    {
      header: 'Approve Note',
      field: 'notes',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        const fData = formData.value.get(data._id);

        return {
          component: TSInputText,
          props: {
            'value': fData?.notes,
            'placeholder': 'Enter note',
            'useValidator': true,
            'modelValue': fData?.notes,
            'onUpdate:modelValue': (value: string): void => {
              if (fData) {
                formData.value.set(data._id, {
                  ...fData,
                  notes: value,
                });
              }
            },
          },
        };
      },
    },
    {
      header: 'Approval',
      field: 'isApproved',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequestFormData,
      ): TableCellComponent => {
        const fData = formData.value.get(data._id);

        return {
          component: TSButtonToggle,
          props: {
            'triState': true,
            'modelValue': fData?.isApproved,
            'onUpdate:modelValue': (value: boolean): void => {
              if (fData) {
                formData.value.set(data._id, {
                  ...fData,
                  isApproved: value,
                });
              }
            },
          },
        };
      },
    },
  ]);
};

export const borrowedColumns = (): ExtendedTableColumn[] => {
  const columns: ExtendedTableColumn[] = [
    {
      field: 'assetImageSmall',
      header: 'Photo',
      fixed: false,
      sortable: true,
      excluded: true,
      bodyComponent: (data: BorrowingBorrowedByAsset): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      field: 'name.nameWithSequence',
      header: 'Name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingBorrowedByAsset): string => {
        return data.name.nameWithSequence ?? '-';
      },
    },
    {
      field: 'status',
      header: 'Status',
      fixed: true,
      sortable: true,
      bodyComponent: (data: BorrowingBorrowedByAsset): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: data.status,
            format: 'nowrap',
            severity: getStatusSeverity(
              data as unknown as BorrowingTransactionRequest,
              'status',
            ),
          },
        };
      },
    },
    {
      field: 'group.name',
      header: 'Group',
      fixed: false,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByAsset): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.group.name,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'borrower.fullName',
      header: 'Borrower',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByAsset): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.borrower.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'manager.fullName',
      header: 'Manager',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByAsset): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.manager.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'updatedAt',
      header: 'Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate(data: BorrowingBorrowedByAsset): string {
        return data.updatedAt ? formatDate(data.updatedAt, true) : '-';
      },
    },
    {
      field: 'duration',
      header: 'Duration',
      fixed: false,
      sortable: true,
      bodyTemplate(data: BorrowingBorrowedByAsset): string {
        return `${data.duration ?? '-'} Day(s)`;
      },
    },
    {
      field: 'dueDate',
      header: 'Due Return Date',
      fixed: false,
      sortable: true,
      dateValue: true,
      bodyTemplate(data: BorrowingBorrowedByAsset): string {
        return data.dueDate ? formatDate(data.dueDate) : '-';
      },
    },
    {
      field: 'remainingDay',
      header: 'Note',
      fixed: false,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByAsset): TableCellComponent {
        return data.remainingDay > -1
          ? {
              component: h(
                'span',
                null,
                `Remaining Time: ${data.remainingDay} day(s)`,
              ),
            }
          : {
              component: h(
                'span',
                {
                  class: 'text-danger-500',
                },
                `Overtime: ${data.remainingDay} day(s)`,
              ),
            };
      },
    },
  ];

  return columns;
};

export const borrowerColumns = (): TableColumn[] => {
  return [
    {
      field: 'borrower.fullName',
      header: 'Borrower',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByBorrower): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.borrower.fullName,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      field: 'group.name',
      header: 'Group',
      fixed: true,
      sortable: true,
      bodyComponent(data: BorrowingBorrowedByBorrower): TableCellComponent {
        return {
          component: TSBadge,
          props: {
            label: data.group.name,
            format: 'nowrap',
            severity: 'primary',
          },
        };
      },
    },
    {
      header: 'Total Asset',
      field: 'totalAsset',
      fixed: false,
      sortable: true,
      bodyTemplate: (data: BorrowingBorrowedByBorrower): string => {
        return data.totalAsset ? data.totalAsset.toLocaleString() : '-';
      },
    },
  ];
};

export const returnColumns = (): TableColumn[] => {
  return [
    {
      header: 'Photo',
      field: 'assetImageSmall',
      fixed: true,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      header: 'Name',
      field: 'name.nameWithSequence',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingBorrowedReturnAsset): string => {
        return data.name.nameWithSequence ? data.name.nameWithSequence : '-';
      },
    },
    {
      header: 'Brand',
      field: 'brand.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return data.brand.name
          ? {
              component: TSBadge,
              props: {
                label: data.brand.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'Model/Type',
      field: 'model.name',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return data.model.name
          ? {
              component: TSBadge,
              props: {
                label: data.model.name,
                severity: 'dark',
                format: 'nowrap',
              },
            }
          : {
              component: EmptyComponent,
            };
      },
    },
    {
      header: 'TAG',
      field: 'tagType',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return {
          component: TSTagType,
          props: {
            qr: data.qr,
            rfid: data.rfid,
          },
        };
      },
    },
    {
      header: 'Due Return Date',
      field: 'dueDate',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return data.remainingDay > -1
          ? {
              component: h('span', null, formatDate(data.dueDate, false)),
            }
          : {
              component: h(
                'span',
                { class: 'text-danger-500' },
                formatDate(data.dueDate, false),
              ),
            };
      },
    },
    {
      header: 'Status',
      field: 'isVerified',
      fixed: true,
      sortable: true,
      bodyComponent: (
        data: BorrowingBorrowedReturnAsset,
      ): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: ((): string => {
              if (data.status === 'TAG Reported') {
                return data.status;
              }
              return data.isVerified ? 'Verified' : 'Unverified';
            })(),
            severity: ((): string => {
              if (data.status === 'TAG Reported') {
                return 'danger';
              }
              return data.isVerified ? 'success' : 'warning';
            })(),
            format: 'nowrap',
          },
        };
      },
    },
  ];
};

const getStatusLabel = (data: BorrowingRequestHandover): string => {
  if (data.status === 'TAG Reported') {
    return data.status;
  }
  return data.isVerified ? 'Verified' : 'Unverified';
};

const getStatusSeverity = (
  data: BorrowingRequestHandover | BorrowingTransactionRequest,
  field: 'isVerified' | 'status',
): string => {
  if (field === 'isVerified') {
    if (data.status === 'TAG Reported') {
      return 'danger';
    }
    return (data as BorrowingRequestHandover).isVerified
      ? 'success'
      : 'warning';
  }

  switch (data.status) {
    case 'Waiting for Approval':
    case 'Waiting for Handover':
    case 'Extend Approval':
    case 'Request Extension':
    case 'Waiting for Confirmation':
      return 'warning';
    case 'Returned':
    case 'Approved':
      return 'success';
    case 'Rejected':
    case 'Reported Damaged':
    case 'Reported Missing':
    case 'TAG Reported':
      return 'danger';
    default:
      return 'primary';
  }
};

export const childColumns = (
  isVerification?: boolean,
  withStatus: boolean = true,
): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      field: 'assetImageSmall',
      header: 'Photo',
      fixed: false,
      sortable: true,
      excluded: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSImage,
          props: {
            thumbnail: getImageURL(data.assetImageSmall),
            preview: getImageURL(data.assetImageBig),
            class: '!w-7 !h-7',
          },
        };
      },
    },
    {
      field: 'name.nameWithSequence',
      header: 'Name',
      fixed: true,
      sortable: true,
      bodyTemplate: (data: BorrowingTransactionRequest): string => {
        return optionalValue(data.name?.nameWithSequence);
      },
    },
    {
      field: 'brand.name',
      header: 'Brand',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: optionalValue(data.brand?.name),
            severity: 'dark',
            format: 'nowrap',
          },
        };
      },
    },
    {
      field: 'model.name',
      header: 'Model/Type',
      fixed: false,
      sortable: true,
      bodyComponent: (
        data: BorrowingTransactionRequest,
      ): TableCellComponent => {
        return {
          component: TSBadge,
          props: {
            label: optionalValue(data.model?.name),
            severity: 'dark',
            format: 'nowrap',
          },
        };
      },
    },
  ];

  if (withStatus) {
    if (isVerification) {
      columns.push({
        header: 'Status',
        field: 'isVerified',
        fixed: true,
        sortable: true,
        bodyComponent: (data: BorrowingRequestHandover): TableCellComponent => {
          return {
            component: TSBadge,
            props: {
              label: getStatusLabel(data),
              severity: getStatusSeverity(data, 'isVerified'),
              format: 'nowrap',
            },
          };
        },
      });
    } else {
      columns.push({
        header: 'Status',
        field: 'status',
        fixed: true,
        sortable: true,
        bodyComponent: (
          data: BorrowingTransactionRequest,
        ): TableCellComponent => {
          const severity = getStatusSeverity(data, 'status');

          return {
            component: TSBadge,
            props: {
              label: data.status,
              severity,
              format: 'nowrap',
            },
          };
        },
      });
    }
  }

  return columns;
};
